import React, { useState, useCallback, useRef, useEffect } from 'react';
import {
  X, Plus, Trash2, ChevronUp, ChevronDown, Copy, Eye, Smartphone, Monitor,
  Type, Image, Link2, Minus, Columns, Share2, Mail, Palette, GripVertical,
  Square, AlignLeft, AlignCenter, AlignRight, ChevronLeft, Check, Sparkles,
  LayoutTemplate, MousePointerClick, ArrowRight, RefreshCw
} from 'lucide-react';

const MERGE_TAGS = [
  { tag: '{{first_name}}', label: 'First Name' },
  { tag: '{{last_name}}', label: 'Last Name' },
  { tag: '{{email}}', label: 'Email' },
  { tag: '{{site_name}}', label: 'Site Name' },
  { tag: '{{member_number}}', label: 'Member #' },
  { tag: '{{amount_due}}', label: 'Amount Due' },
  { tag: '{{unsubscribe_link}}', label: 'Unsub Link' },
];

const ULTRAFLEX_LOGO = '/ultraflex-logo.png';

const DEFAULT_BRAND = {
  primaryColor: '#E31837',
  secondaryColor: '#050505',
  bgColor: '#050505',
  textColor: '#1f2937',
  fontFamily: "'Trebuchet MS', Arial, Helvetica, sans-serif",
  logoUrl: ULTRAFLEX_LOGO,
  companyName: 'UltraFlex Gym',
};

const BLOCK_TYPES = [
  { type: 'header', label: 'Header Banner', icon: LayoutTemplate, desc: 'Logo & headline' },
  { type: 'text', label: 'Text Block', icon: Type, desc: 'Paragraphs & formatting' },
  { type: 'button', label: 'CTA Button', icon: MousePointerClick, desc: 'Call-to-action' },
  { type: 'image', label: 'Image', icon: Image, desc: 'Image with caption' },
  { type: 'divider', label: 'Divider', icon: Minus, desc: 'Horizontal line' },
  { type: 'spacer', label: 'Spacer', icon: Square, desc: 'Vertical spacing' },
  { type: 'columns', label: 'Two Columns', icon: Columns, desc: 'Side-by-side content' },
  { type: 'social', label: 'Social Links', icon: Share2, desc: 'Social media icons' },
  { type: 'footer', label: 'Footer', icon: Mail, desc: 'Unsub & company info' },
];

const createBlock = (type) => {
  const id = `${type}-${Date.now()}`;
  const defaults = {
    header: { id, type, content: { headline: 'Your Headline Here', subheadline: '', bgColor: '#050505', textColor: '#ffffff', padding: '40', alignment: 'center', showLogo: true, logoUrl: '' } },
    text: { id, type, content: { text: 'Write your message here. Use merge tags to personalise.', fontSize: '16', color: '#374151', lineHeight: '1.6', padding: '20', alignment: 'left' } },
    button: { id, type, content: { text: 'Take Action', url: '#', bgColor: '#E31837', textColor: '#ffffff', borderRadius: '6', size: 'large', alignment: 'center', fullWidth: false, padding: '20' } },
    image: { id, type, content: { url: '', alt: 'Image', width: '100', alignment: 'center', caption: '', borderRadius: '8', padding: '20' } },
    divider: { id, type, content: { color: '#e5e7eb', thickness: '1', width: '100', style: 'solid', padding: '15' } },
    spacer: { id, type, content: { height: '30' } },
    columns: { id, type, content: { leftText: 'Left column content', rightText: 'Right column content', gap: '20', padding: '20', valign: 'top' } },
    social: { id, type, content: { alignment: 'center', padding: '20', iconSize: '32', links: { facebook: '', instagram: '', twitter: '', youtube: '', linkedin: '' } } },
    footer: { id, type, content: { text: '{{companyName}} | {{address}}', unsubText: 'Unsubscribe from these emails', textColor: '#9ca3af', fontSize: '12', padding: '30', bgColor: '#050505' } },
  };
  return defaults[type] || { id, type, content: {} };
};

const EMAIL_PRESETS = [
  {
    id: 'welcome',
    name: 'Welcome Email',
    desc: 'Branded header, warm intro, CTA',
    blocks: [
      { ...createBlock('header'), content: { headline: 'Welcome to UltraFlex!', subheadline: 'Your fitness journey starts here', bgColor: '#050505', textColor: '#ffffff', padding: '50', alignment: 'center', showLogo: true, logoUrl: '' } },
      { ...createBlock('spacer'), content: { height: '10' } },
      { ...createBlock('text'), content: { text: 'Hi {{first_name}},\n\nWe\'re thrilled to have you! Whether you\'re a seasoned gym-goer or just starting out, our team at {{site_name}} is here to support every step of your fitness journey.\n\nHere\'s what you can look forward to:', fontSize: '16', color: '#374151', lineHeight: '1.7', padding: '30', alignment: 'left' } },
      { ...createBlock('columns'), content: { leftText: 'State-of-the-art equipment\nacross all 11 locations', rightText: 'No lock-in contracts\nwith flexible memberships', gap: '20', padding: '20', valign: 'top' } },
      { ...createBlock('columns'), content: { leftText: 'Free fitness assessment\nwith a certified PT', rightText: '24/7 access\nat most locations', gap: '20', padding: '10', valign: 'top' } },
      { ...createBlock('spacer'), content: { height: '10' } },
      { ...createBlock('button'), content: { text: 'Explore Your Membership', url: '#', bgColor: '#E31837', textColor: '#ffffff', borderRadius: '6', size: 'large', alignment: 'center', fullWidth: false, padding: '10' } },
      { ...createBlock('divider'), content: { color: '#e5e7eb', thickness: '1', width: '80', style: 'solid', padding: '25' } },
      { ...createBlock('text'), content: { text: 'Need help getting started? Reply to this email or pop into {{site_name}} — we\'re always happy to help.', fontSize: '14', color: '#6b7280', lineHeight: '1.6', padding: '15', alignment: 'center' } },
      { ...createBlock('social'), content: { alignment: 'center', padding: '15', iconSize: '32', links: { facebook: 'https://facebook.com/ultraflex', instagram: 'https://instagram.com/ultraflex', twitter: '', youtube: '', linkedin: '' } } },
      { ...createBlock('footer'), content: { text: 'UltraFlex Gym | Empowering your fitness journey\n11 locations across the UK', unsubText: '{{unsubscribe_link}}', textColor: '#9ca3af', fontSize: '12', padding: '30', bgColor: '#050505' } },
    ],
  },
  {
    id: 'promotional',
    name: 'Promotional Offer',
    desc: 'Bold offer, feature highlights, urgency CTA',
    blocks: [
      { ...createBlock('header'), content: { headline: 'Limited Time Offer', subheadline: 'Exclusively for you, {{first_name}}', bgColor: '#050505', textColor: '#ffffff', padding: '50', alignment: 'center', showLogo: true, logoUrl: '' } },
      { ...createBlock('spacer'), content: { height: '10' } },
      { ...createBlock('text'), content: { text: 'Hi {{first_name}},\n\nFor a limited time, we\'re offering an incredible deal on membership at {{site_name}}. This is your chance to join the UltraFlex community at an unbeatable price.', fontSize: '16', color: '#374151', lineHeight: '1.7', padding: '30', alignment: 'left' } },
      { ...createBlock('divider'), content: { color: '#E31837', thickness: '2', width: '40', style: 'solid', padding: '10' } },
      { ...createBlock('columns'), content: { leftText: 'No Joining Fee\nSave on your first month', rightText: 'Free PT Session\nPersonalised fitness plan', gap: '20', padding: '20', valign: 'top' } },
      { ...createBlock('spacer'), content: { height: '10' } },
      { ...createBlock('button'), content: { text: 'Claim Your Offer Now', url: '#', bgColor: '#E31837', textColor: '#ffffff', borderRadius: '6', size: 'large', alignment: 'center', fullWidth: true, padding: '20' } },
      { ...createBlock('text'), content: { text: 'Offer ends soon. Terms and conditions apply.', fontSize: '12', color: '#9ca3af', lineHeight: '1.5', padding: '10', alignment: 'center' } },
      { ...createBlock('footer'), content: { text: 'UltraFlex Gym | Empowering your fitness journey\n11 locations across the UK', unsubText: '{{unsubscribe_link}}', textColor: '#9ca3af', fontSize: '12', padding: '30', bgColor: '#050505' } },
    ],
  },
  {
    id: 'informational',
    name: 'Information Update',
    desc: 'Clean branded update with clear structure',
    blocks: [
      { ...createBlock('header'), content: { headline: 'Important Update', subheadline: 'From your team at {{site_name}}', bgColor: '#050505', textColor: '#ffffff', padding: '40', alignment: 'center', showLogo: true, logoUrl: '' } },
      { ...createBlock('text'), content: { text: 'Hi {{first_name}},\n\nWe wanted to keep you in the loop about some updates at {{site_name}}.', fontSize: '16', color: '#374151', lineHeight: '1.7', padding: '30', alignment: 'left' } },
      { ...createBlock('divider'), content: { color: '#e5e7eb', thickness: '1', width: '100', style: 'solid', padding: '10' } },
      { ...createBlock('text'), content: { text: 'Add your update details here. You can include multiple text blocks, images, and buttons to structure your message effectively.', fontSize: '15', color: '#4b5563', lineHeight: '1.6', padding: '20', alignment: 'left' } },
      { ...createBlock('button'), content: { text: 'Learn More', url: '#', bgColor: '#E31837', textColor: '#ffffff', borderRadius: '6', size: 'medium', alignment: 'center', fullWidth: false, padding: '20' } },
      { ...createBlock('footer'), content: { text: 'UltraFlex Gym | Empowering your fitness journey\n11 locations across the UK', unsubText: '{{unsubscribe_link}}', textColor: '#9ca3af', fontSize: '12', padding: '30', bgColor: '#050505' } },
    ],
  },
  {
    id: 'winback',
    name: 'Win-Back / Re-Engage',
    desc: 'Warm outreach with incentive offer',
    blocks: [
      { ...createBlock('header'), content: { headline: 'We Miss You, {{first_name}}!', subheadline: 'Come back and see what\'s new', bgColor: '#050505', textColor: '#ffffff', padding: '50', alignment: 'center', showLogo: true, logoUrl: '' } },
      { ...createBlock('spacer'), content: { height: '10' } },
      { ...createBlock('text'), content: { text: 'Hi {{first_name}},\n\nIt\'s been a while since we\'ve seen you at {{site_name}}, and we just wanted to say — we miss having you around!\n\nLife gets busy, but your fitness goals don\'t have to wait. Here\'s what\'s been happening while you\'ve been away:', fontSize: '16', color: '#374151', lineHeight: '1.7', padding: '30', alignment: 'left' } },
      { ...createBlock('columns'), content: { leftText: 'New Equipment\nState-of-the-art gym floor', rightText: 'New Classes\nRefreshed group fitness schedule', gap: '20', padding: '20', valign: 'top' } },
      { ...createBlock('divider'), content: { color: '#E31837', thickness: '2', width: '40', style: 'solid', padding: '15' } },
      { ...createBlock('text'), content: { text: 'As a special welcome back, we\'d like to offer you a complimentary personal training session. No strings attached.', fontSize: '16', color: '#374151', lineHeight: '1.6', padding: '15', alignment: 'center' } },
      { ...createBlock('button'), content: { text: 'Book Your Free Session', url: '#', bgColor: '#E31837', textColor: '#ffffff', borderRadius: '6', size: 'large', alignment: 'center', fullWidth: false, padding: '15' } },
      { ...createBlock('social'), content: { alignment: 'center', padding: '15', iconSize: '32', links: { facebook: 'https://facebook.com/ultraflex', instagram: 'https://instagram.com/ultraflex', twitter: '', youtube: '', linkedin: '' } } },
      { ...createBlock('footer'), content: { text: 'UltraFlex Gym | Empowering your fitness journey\n11 locations across the UK', unsubText: '{{unsubscribe_link}}', textColor: '#9ca3af', fontSize: '12', padding: '30', bgColor: '#050505' } },
    ],
  },
  {
    id: 'blank',
    name: 'Blank Template',
    desc: 'Start from scratch',
    blocks: [],
  },
];

// ===================== BLOCK RENDERER (for preview) =====================
const renderBlockHtml = (block, brand) => {
  const c = block.content;
  const wrap = (inner, padding) => `<div style="padding: ${padding || '0'}px ${padding || 20}px;">${inner}</div>`;

  switch (block.type) {
    case 'header': {
      const logoSrc = c.logoUrl || brand.logoUrl;
      const showLogo = c.showLogo !== false && logoSrc;
      return `
        <div style="background-color: ${c.bgColor || brand.primaryColor}; padding: ${c.padding || 40}px 30px; text-align: ${c.alignment || 'center'};">
          ${showLogo ? `<img src="${logoSrc}" alt="${brand.companyName}" style="max-height: 50px; margin-bottom: 20px;" />` : ''}
          <h1 style="margin: 0; font-size: 28px; font-weight: 700; color: ${c.textColor || '#fff'}; font-family: ${brand.fontFamily}; line-height: 1.3; letter-spacing: 1px; text-transform: uppercase;">${c.headline || ''}</h1>
          ${c.subheadline ? `<p style="margin: 12px 0 0; font-size: 15px; color: ${c.textColor || '#fff'}; opacity: 0.85; font-family: ${brand.fontFamily}; letter-spacing: 0.3px;">${c.subheadline}</p>` : ''}
        </div>`;
    }
    case 'text':
      return wrap(`<div style="font-size: ${c.fontSize || 16}px; color: ${c.color || brand.textColor}; line-height: ${c.lineHeight || 1.6}; font-family: ${brand.fontFamily}; text-align: ${c.alignment || 'left'}; white-space: pre-line;">${(c.text || '').replace(/\n/g, '<br/>')}</div>`, c.padding);
    case 'button': {
      const sizes = { small: '10px 20px; font-size: 13px', medium: '13px 28px; font-size: 15px', large: '16px 36px; font-size: 16px' };
      const w = c.fullWidth ? 'display: block; width: 100%; box-sizing: border-box;' : 'display: inline-block;';
      return wrap(`<div style="text-align: ${c.alignment || 'center'};">
        <a href="${c.url || '#'}" style="${w} background-color: ${c.bgColor || brand.primaryColor}; color: ${c.textColor || '#fff'}; text-decoration: none; padding: ${sizes[c.size] || sizes.large}; border-radius: ${c.borderRadius || 6}px; font-weight: 600; font-family: ${brand.fontFamily}; letter-spacing: 0.3px; text-align: center;">${c.text || 'Click Here'}</a>
      </div>`, c.padding);
    }
    case 'image':
      return wrap(`<div style="text-align: ${c.alignment || 'center'};">
        ${c.url ? `<img src="${c.url}" alt="${c.alt || ''}" style="max-width: ${c.width || 100}%; border-radius: ${c.borderRadius || 0}px; display: inline-block;" />` : `<div style="background: #e5e7eb; border-radius: ${c.borderRadius || 8}px; padding: 40px; text-align: center; color: #9ca3af; font-size: 14px; font-family: ${brand.fontFamily};">Image placeholder — add URL above</div>`}
        ${c.caption ? `<p style="margin: 8px 0 0; font-size: 13px; color: #9ca3af; font-family: ${brand.fontFamily};">${c.caption}</p>` : ''}
      </div>`, c.padding);
    case 'divider':
      return `<div style="padding: ${c.padding || 15}px 20px;"><hr style="border: none; border-top: ${c.thickness || 1}px ${c.style || 'solid'} ${c.color || '#e5e7eb'}; width: ${c.width || 100}%; margin: 0 auto;" /></div>`;
    case 'spacer':
      return `<div style="height: ${c.height || 30}px;"></div>`;
    case 'columns':
      return wrap(`<table width="100%" cellpadding="0" cellspacing="0" border="0"><tr>
        <td style="width: 48%; vertical-align: ${c.valign || 'top'}; padding: 12px; font-size: 15px; color: ${brand.textColor}; font-family: ${brand.fontFamily}; line-height: 1.5; white-space: pre-line;">${(c.leftText || '').replace(/\n/g, '<br/>')}</td>
        <td style="width: 4%;"></td>
        <td style="width: 48%; vertical-align: ${c.valign || 'top'}; padding: 12px; font-size: 15px; color: ${brand.textColor}; font-family: ${brand.fontFamily}; line-height: 1.5; white-space: pre-line;">${(c.rightText || '').replace(/\n/g, '<br/>')}</td>
      </tr></table>`, c.padding);
    case 'social': {
      const icons = Object.entries(c.links || {}).filter(([, v]) => v);
      const labels = { facebook: 'Facebook', instagram: 'Instagram', twitter: 'X / Twitter', youtube: 'YouTube', linkedin: 'LinkedIn' };
      return wrap(`<div style="text-align: ${c.alignment || 'center'};">
        ${icons.length > 0 ? icons.map(([k, url]) => `<a href="${url}" style="display: inline-block; margin: 0 6px; color: ${brand.primaryColor}; text-decoration: none; font-size: 13px; font-family: ${brand.fontFamily};">${labels[k] || k}</a>`).join(' &bull; ') : '<span style="color: #9ca3af; font-size: 13px;">Add social links in the editor</span>'}
      </div>`, c.padding);
    }
    case 'footer':
      return `<div style="background-color: ${c.bgColor || '#050505'}; padding: ${c.padding || 30}px 30px; text-align: center;">
        ${brand.logoUrl ? `<img src="${brand.logoUrl}" alt="${brand.companyName}" style="max-height: 30px; margin-bottom: 16px; opacity: 0.6;" />` : ''}
        <p style="margin: 0 0 8px; font-size: ${c.fontSize || 12}px; color: ${c.textColor || '#9ca3af'}; font-family: ${brand.fontFamily}; line-height: 1.6; white-space: pre-line;">${(c.text || '').replace(/\n/g, '<br/>')}</p>
        <p style="margin: 0; font-size: 11px;"><a href="#" style="color: ${c.textColor || '#9ca3af'}; text-decoration: underline; font-family: ${brand.fontFamily};">${c.unsubText || 'Unsubscribe'}</a></p>
      </div>`;
    default:
      return '';
  }
};

const generateFullHtml = (blocks, brand) => {
  const body = blocks.map(b => renderBlockHtml(b, brand)).join('');
  return `<!DOCTYPE html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width, initial-scale=1.0"><title>Email</title></head>
<body style="margin: 0; padding: 0; background-color: ${brand.bgColor}; font-family: ${brand.fontFamily}; -webkit-font-smoothing: antialiased;">
<div style="max-width: 600px; margin: 0 auto; background-color: #ffffff; border-radius: 0;">
${body}
</div></body></html>`;
};

// ===================== BLOCK EDITOR =====================
const BlockEditor = ({ block, onChange, onDelete, onMoveUp, onMoveDown, isFirst, isLast, brand }) => {
  const [expanded, setExpanded] = useState(false);
  const c = block.content;
  const upd = (key, val) => onChange({ ...block, content: { ...c, [key]: val } });
  const blockType = BLOCK_TYPES.find(b => b.type === block.type);
  const Icon = blockType?.icon || Type;

  const insertMergeTag = (field, tag) => upd(field, (c[field] || '') + tag);

  const MergeTagRow = ({ field }) => (
    <div className="mt-1.5">
      <div className="flex flex-wrap gap-1">
        {MERGE_TAGS.map(mt => (
          <button key={mt.tag} type="button" onClick={() => insertMergeTag(field, mt.tag)}
            className="px-1.5 py-0.5 bg-emerald-500/10 text-emerald-400 rounded text-[9px] font-mono hover:bg-emerald-500/20 transition-colors">{mt.label}</button>
        ))}
      </div>
    </div>
  );

  return (
    <div className={`group border rounded-lg transition-all ${expanded ? 'border-[#dc2626]/50 bg-[#111827]' : 'border-[#1e293b] bg-[#0f172a] hover:border-[#2a3a5a]'}`}
      data-testid={`email-block-${block.id}`}>
      {/* Block header */}
      <div className="flex items-center gap-2 px-3 py-2 cursor-pointer" onClick={() => setExpanded(!expanded)}>
        <GripVertical size={14} className="text-gray-600 flex-shrink-0" />
        <div className="p-1 rounded" style={{ background: `${brand.primaryColor}15` }}>
          <Icon size={12} style={{ color: brand.primaryColor }} />
        </div>
        <span className="text-white text-xs font-medium flex-1">{blockType?.label || block.type}</span>
        <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
          {!isFirst && <button onClick={e => { e.stopPropagation(); onMoveUp(); }} className="p-1 rounded hover:bg-[#1e293b] text-gray-500"><ChevronUp size={12} /></button>}
          {!isLast && <button onClick={e => { e.stopPropagation(); onMoveDown(); }} className="p-1 rounded hover:bg-[#1e293b] text-gray-500"><ChevronDown size={12} /></button>}
          <button onClick={e => { e.stopPropagation(); onDelete(); }} className="p-1 rounded hover:bg-red-500/20 text-gray-500 hover:text-red-400"><Trash2 size={12} /></button>
        </div>
        {expanded ? <ChevronUp size={14} className="text-gray-500" /> : <ChevronDown size={14} className="text-gray-500" />}
      </div>

      {/* Block content editor */}
      {expanded && (
        <div className="px-3 pb-3 space-y-3 border-t border-[#1e293b] pt-3">
          {block.type === 'header' && (
            <>
              <div>
                <label className="text-gray-400 text-[10px] font-medium block mb-1">Headline</label>
                <input value={c.headline || ''} onChange={e => upd('headline', e.target.value)}
                  className="w-full bg-[#0b0f1a] border border-[#1e293b] rounded px-3 py-2 text-white text-sm font-semibold" />
                <MergeTagRow field="headline" />
              </div>
              <div>
                <label className="text-gray-400 text-[10px] font-medium block mb-1">Subheadline</label>
                <input value={c.subheadline || ''} onChange={e => upd('subheadline', e.target.value)} placeholder="Optional secondary text"
                  className="w-full bg-[#0b0f1a] border border-[#1e293b] rounded px-3 py-2 text-white text-xs" />
                <MergeTagRow field="subheadline" />
              </div>
              <div className="flex items-center justify-between p-2 bg-[#0b0f1a] rounded-lg border border-[#1e293b]">
                <span className="text-gray-400 text-[10px]">Show Logo</span>
                <button onClick={() => upd('showLogo', c.showLogo === false ? true : false)}
                  className={`w-8 h-4 rounded-full transition-colors ${c.showLogo !== false ? 'bg-[#22c55e]' : 'bg-gray-600'}`}>
                  <div className={`w-3 h-3 rounded-full bg-white transition-transform ${c.showLogo !== false ? 'translate-x-4' : 'translate-x-0.5'}`} />
                </button>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-gray-400 text-[10px] font-medium block mb-1">Background</label>
                  <div className="flex items-center gap-2">
                    <input type="color" value={c.bgColor || '#dc2626'} onChange={e => upd('bgColor', e.target.value)} className="w-8 h-8 rounded cursor-pointer bg-transparent border-0" />
                    <input value={c.bgColor || '#dc2626'} onChange={e => upd('bgColor', e.target.value)} className="flex-1 bg-[#0b0f1a] border border-[#1e293b] rounded px-2 py-1 text-white text-[10px] font-mono" />
                  </div>
                </div>
                <div>
                  <label className="text-gray-400 text-[10px] font-medium block mb-1">Text Color</label>
                  <div className="flex items-center gap-2">
                    <input type="color" value={c.textColor || '#ffffff'} onChange={e => upd('textColor', e.target.value)} className="w-8 h-8 rounded cursor-pointer bg-transparent border-0" />
                    <input value={c.textColor || '#ffffff'} onChange={e => upd('textColor', e.target.value)} className="flex-1 bg-[#0b0f1a] border border-[#1e293b] rounded px-2 py-1 text-white text-[10px] font-mono" />
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-gray-400 text-[10px] font-medium block mb-1">Alignment</label>
                  <div className="flex gap-1">
                    {['left', 'center', 'right'].map(a => (
                      <button key={a} onClick={() => upd('alignment', a)}
                        className={`flex-1 py-1.5 rounded text-xs ${c.alignment === a ? 'bg-[#dc2626] text-white' : 'bg-[#1e293b] text-gray-400 hover:text-white'}`}>
                        {a === 'left' ? <AlignLeft size={12} className="mx-auto" /> : a === 'center' ? <AlignCenter size={12} className="mx-auto" /> : <AlignRight size={12} className="mx-auto" />}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="text-gray-400 text-[10px] font-medium block mb-1">Padding</label>
                  <input type="range" min="20" max="80" value={c.padding || 40} onChange={e => upd('padding', e.target.value)} className="w-full accent-[#dc2626]" />
                </div>
              </div>
            </>
          )}

          {block.type === 'text' && (
            <>
              <div>
                <label className="text-gray-400 text-[10px] font-medium block mb-1">Content</label>
                <textarea value={c.text || ''} onChange={e => upd('text', e.target.value)} rows={5}
                  className="w-full bg-[#0b0f1a] border border-[#1e293b] rounded px-3 py-2 text-white text-xs leading-relaxed resize-none" />
                <MergeTagRow field="text" />
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <label className="text-gray-400 text-[10px] font-medium block mb-1">Font Size</label>
                  <select value={c.fontSize || '16'} onChange={e => upd('fontSize', e.target.value)}
                    className="w-full bg-[#0b0f1a] border border-[#1e293b] rounded px-2 py-1.5 text-white text-[10px]">
                    {['12', '13', '14', '15', '16', '18', '20', '22', '24'].map(s => <option key={s} value={s}>{s}px</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-gray-400 text-[10px] font-medium block mb-1">Color</label>
                  <div className="flex items-center gap-1">
                    <input type="color" value={c.color || '#374151'} onChange={e => upd('color', e.target.value)} className="w-6 h-6 rounded cursor-pointer bg-transparent border-0" />
                    <input value={c.color || '#374151'} onChange={e => upd('color', e.target.value)} className="flex-1 bg-[#0b0f1a] border border-[#1e293b] rounded px-1 py-1 text-white text-[9px] font-mono" />
                  </div>
                </div>
                <div>
                  <label className="text-gray-400 text-[10px] font-medium block mb-1">Align</label>
                  <div className="flex gap-0.5">
                    {['left', 'center', 'right'].map(a => (
                      <button key={a} onClick={() => upd('alignment', a)}
                        className={`flex-1 py-1.5 rounded text-[10px] ${c.alignment === a ? 'bg-[#dc2626] text-white' : 'bg-[#1e293b] text-gray-400'}`}>
                        {a[0].toUpperCase()}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </>
          )}

          {block.type === 'button' && (
            <>
              <div>
                <label className="text-gray-400 text-[10px] font-medium block mb-1">Button Text</label>
                <input value={c.text || ''} onChange={e => upd('text', e.target.value)}
                  className="w-full bg-[#0b0f1a] border border-[#1e293b] rounded px-3 py-2 text-white text-sm font-semibold" />
              </div>
              <div>
                <label className="text-gray-400 text-[10px] font-medium block mb-1">Link URL</label>
                <input value={c.url || ''} onChange={e => upd('url', e.target.value)} placeholder="https://..."
                  className="w-full bg-[#0b0f1a] border border-[#1e293b] rounded px-3 py-2 text-white text-xs" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-gray-400 text-[10px] font-medium block mb-1">Button Color</label>
                  <div className="flex items-center gap-2">
                    <input type="color" value={c.bgColor || '#dc2626'} onChange={e => upd('bgColor', e.target.value)} className="w-8 h-8 rounded cursor-pointer bg-transparent border-0" />
                    <input value={c.bgColor || '#dc2626'} onChange={e => upd('bgColor', e.target.value)} className="flex-1 bg-[#0b0f1a] border border-[#1e293b] rounded px-2 py-1 text-white text-[10px] font-mono" />
                  </div>
                </div>
                <div>
                  <label className="text-gray-400 text-[10px] font-medium block mb-1">Text Color</label>
                  <div className="flex items-center gap-2">
                    <input type="color" value={c.textColor || '#ffffff'} onChange={e => upd('textColor', e.target.value)} className="w-8 h-8 rounded cursor-pointer bg-transparent border-0" />
                    <input value={c.textColor || '#ffffff'} onChange={e => upd('textColor', e.target.value)} className="flex-1 bg-[#0b0f1a] border border-[#1e293b] rounded px-2 py-1 text-white text-[10px] font-mono" />
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <label className="text-gray-400 text-[10px] font-medium block mb-1">Size</label>
                  <select value={c.size || 'large'} onChange={e => upd('size', e.target.value)}
                    className="w-full bg-[#0b0f1a] border border-[#1e293b] rounded px-2 py-1.5 text-white text-[10px]">
                    <option value="small">Small</option>
                    <option value="medium">Medium</option>
                    <option value="large">Large</option>
                  </select>
                </div>
                <div>
                  <label className="text-gray-400 text-[10px] font-medium block mb-1">Shape</label>
                  <select value={c.borderRadius || '6'} onChange={e => upd('borderRadius', e.target.value)}
                    className="w-full bg-[#0b0f1a] border border-[#1e293b] rounded px-2 py-1.5 text-white text-[10px]">
                    <option value="0">Square</option>
                    <option value="6">Rounded</option>
                    <option value="50">Pill</option>
                  </select>
                </div>
                <div>
                  <label className="text-gray-400 text-[10px] font-medium block mb-1">Width</label>
                  <button onClick={() => upd('fullWidth', !c.fullWidth)}
                    className={`w-full py-1.5 rounded text-[10px] font-medium ${c.fullWidth ? 'bg-[#dc2626] text-white' : 'bg-[#1e293b] text-gray-400'}`}>
                    {c.fullWidth ? 'Full' : 'Auto'}
                  </button>
                </div>
              </div>
              <div>
                <label className="text-gray-400 text-[10px] font-medium block mb-1">Alignment</label>
                <div className="flex gap-1">
                  {['left', 'center', 'right'].map(a => (
                    <button key={a} onClick={() => upd('alignment', a)}
                      className={`flex-1 py-1.5 rounded text-xs ${c.alignment === a ? 'bg-[#dc2626] text-white' : 'bg-[#1e293b] text-gray-400'}`}>
                      {a === 'left' ? <AlignLeft size={12} className="mx-auto" /> : a === 'center' ? <AlignCenter size={12} className="mx-auto" /> : <AlignRight size={12} className="mx-auto" />}
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}

          {block.type === 'image' && (
            <>
              <div>
                <label className="text-gray-400 text-[10px] font-medium block mb-1">Image URL</label>
                <input value={c.url || ''} onChange={e => upd('url', e.target.value)} placeholder="https://example.com/image.jpg"
                  className="w-full bg-[#0b0f1a] border border-[#1e293b] rounded px-3 py-2 text-white text-xs" />
              </div>
              <div>
                <label className="text-gray-400 text-[10px] font-medium block mb-1">Alt Text</label>
                <input value={c.alt || ''} onChange={e => upd('alt', e.target.value)} placeholder="Describe the image"
                  className="w-full bg-[#0b0f1a] border border-[#1e293b] rounded px-3 py-2 text-white text-xs" />
              </div>
              <div>
                <label className="text-gray-400 text-[10px] font-medium block mb-1">Caption</label>
                <input value={c.caption || ''} onChange={e => upd('caption', e.target.value)} placeholder="Optional caption"
                  className="w-full bg-[#0b0f1a] border border-[#1e293b] rounded px-3 py-2 text-white text-xs" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-gray-400 text-[10px] font-medium block mb-1">Width %</label>
                  <input type="range" min="20" max="100" value={c.width || 100} onChange={e => upd('width', e.target.value)} className="w-full accent-[#dc2626]" />
                  <p className="text-gray-500 text-[9px] text-center">{c.width || 100}%</p>
                </div>
                <div>
                  <label className="text-gray-400 text-[10px] font-medium block mb-1">Corner Radius</label>
                  <input type="range" min="0" max="30" value={c.borderRadius || 0} onChange={e => upd('borderRadius', e.target.value)} className="w-full accent-[#dc2626]" />
                  <p className="text-gray-500 text-[9px] text-center">{c.borderRadius || 0}px</p>
                </div>
              </div>
            </>
          )}

          {block.type === 'divider' && (
            <div className="grid grid-cols-3 gap-2">
              <div>
                <label className="text-gray-400 text-[10px] font-medium block mb-1">Color</label>
                <input type="color" value={c.color || '#e5e7eb'} onChange={e => upd('color', e.target.value)} className="w-full h-8 rounded cursor-pointer bg-transparent border-0" />
              </div>
              <div>
                <label className="text-gray-400 text-[10px] font-medium block mb-1">Style</label>
                <select value={c.style || 'solid'} onChange={e => upd('style', e.target.value)}
                  className="w-full bg-[#0b0f1a] border border-[#1e293b] rounded px-2 py-1.5 text-white text-[10px]">
                  <option value="solid">Solid</option>
                  <option value="dashed">Dashed</option>
                  <option value="dotted">Dotted</option>
                </select>
              </div>
              <div>
                <label className="text-gray-400 text-[10px] font-medium block mb-1">Width %</label>
                <input type="range" min="20" max="100" value={c.width || 100} onChange={e => upd('width', e.target.value)} className="w-full accent-[#dc2626]" />
              </div>
            </div>
          )}

          {block.type === 'spacer' && (
            <div>
              <label className="text-gray-400 text-[10px] font-medium block mb-1">Height: {c.height || 30}px</label>
              <input type="range" min="10" max="100" value={c.height || 30} onChange={e => upd('height', e.target.value)} className="w-full accent-[#dc2626]" />
            </div>
          )}

          {block.type === 'columns' && (
            <>
              <div>
                <label className="text-gray-400 text-[10px] font-medium block mb-1">Left Column</label>
                <textarea value={c.leftText || ''} onChange={e => upd('leftText', e.target.value)} rows={3}
                  className="w-full bg-[#0b0f1a] border border-[#1e293b] rounded px-3 py-2 text-white text-xs resize-none" />
                <MergeTagRow field="leftText" />
              </div>
              <div>
                <label className="text-gray-400 text-[10px] font-medium block mb-1">Right Column</label>
                <textarea value={c.rightText || ''} onChange={e => upd('rightText', e.target.value)} rows={3}
                  className="w-full bg-[#0b0f1a] border border-[#1e293b] rounded px-3 py-2 text-white text-xs resize-none" />
                <MergeTagRow field="rightText" />
              </div>
            </>
          )}

          {block.type === 'social' && (
            <>
              {Object.keys(c.links || {}).map(platform => (
                <div key={platform}>
                  <label className="text-gray-400 text-[10px] font-medium block mb-1 capitalize">{platform}</label>
                  <input value={(c.links || {})[platform] || ''} onChange={e => upd('links', { ...c.links, [platform]: e.target.value })} placeholder={`https://${platform}.com/...`}
                    className="w-full bg-[#0b0f1a] border border-[#1e293b] rounded px-3 py-1.5 text-white text-xs" />
                </div>
              ))}
              <div>
                <label className="text-gray-400 text-[10px] font-medium block mb-1">Alignment</label>
                <div className="flex gap-1">
                  {['left', 'center', 'right'].map(a => (
                    <button key={a} onClick={() => upd('alignment', a)}
                      className={`flex-1 py-1 rounded text-[10px] ${c.alignment === a ? 'bg-[#dc2626] text-white' : 'bg-[#1e293b] text-gray-400'}`}>{a}</button>
                  ))}
                </div>
              </div>
            </>
          )}

          {block.type === 'footer' && (
            <>
              <div>
                <label className="text-gray-400 text-[10px] font-medium block mb-1">Footer Text</label>
                <textarea value={c.text || ''} onChange={e => upd('text', e.target.value)} rows={2}
                  className="w-full bg-[#0b0f1a] border border-[#1e293b] rounded px-3 py-2 text-white text-xs resize-none" />
              </div>
              <div>
                <label className="text-gray-400 text-[10px] font-medium block mb-1">Unsubscribe Text</label>
                <input value={c.unsubText || ''} onChange={e => upd('unsubText', e.target.value)}
                  className="w-full bg-[#0b0f1a] border border-[#1e293b] rounded px-3 py-2 text-white text-xs" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-gray-400 text-[10px] font-medium block mb-1">Background</label>
                  <div className="flex items-center gap-1">
                    <input type="color" value={c.bgColor || '#111827'} onChange={e => upd('bgColor', e.target.value)} className="w-6 h-6 rounded cursor-pointer bg-transparent border-0" />
                    <input value={c.bgColor || '#111827'} onChange={e => upd('bgColor', e.target.value)} className="flex-1 bg-[#0b0f1a] border border-[#1e293b] rounded px-1 py-1 text-white text-[9px] font-mono" />
                  </div>
                </div>
                <div>
                  <label className="text-gray-400 text-[10px] font-medium block mb-1">Text Color</label>
                  <div className="flex items-center gap-1">
                    <input type="color" value={c.textColor || '#9ca3af'} onChange={e => upd('textColor', e.target.value)} className="w-6 h-6 rounded cursor-pointer bg-transparent border-0" />
                    <input value={c.textColor || '#9ca3af'} onChange={e => upd('textColor', e.target.value)} className="flex-1 bg-[#0b0f1a] border border-[#1e293b] rounded px-1 py-1 text-white text-[9px] font-mono" />
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

// ===================== MAIN EMAIL DESIGNER =====================
const EmailDesigner = ({ data, onSave, onClose, token }) => {
  const [blocks, setBlocks] = useState(data?.emailBlocks || []);
  const [brand, setBrand] = useState(data?.brand || DEFAULT_BRAND);
  const [previewMode, setPreviewMode] = useState('desktop');
  const [showPresets, setShowPresets] = useState(blocks.length === 0);
  const [activeTab, setActiveTab] = useState('blocks');
  const [sendTestOpen, setSendTestOpen] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [sendingTest, setSendingTest] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const previewRef = useRef(null);

  const updateBlock = useCallback((idx, updated) => {
    setBlocks(prev => prev.map((b, i) => i === idx ? updated : b));
  }, []);

  const deleteBlock = useCallback((idx) => {
    setBlocks(prev => prev.filter((_, i) => i !== idx));
  }, []);

  const moveBlock = useCallback((idx, dir) => {
    setBlocks(prev => {
      const arr = [...prev];
      const swap = idx + dir;
      if (swap < 0 || swap >= arr.length) return arr;
      [arr[idx], arr[swap]] = [arr[swap], arr[idx]];
      return arr;
    });
  }, []);

  const addBlock = useCallback((type) => {
    setBlocks(prev => [...prev, createBlock(type)]);
  }, []);

  const loadPreset = useCallback((preset) => {
    if (preset.id === 'blank') {
      setBlocks([]);
    } else {
      setBlocks(preset.blocks.map(b => ({ ...b, id: `${b.type}-${Date.now()}-${Math.random().toString(36).substr(2, 5)}` })));
    }
    setShowPresets(false);
  }, []);

  const handleSendTest = async () => {
    if (!testEmail) return;
    setSendingTest(true);
    setTestResult(null);
    try {
      const html = generateFullHtml(blocks, brand);
      const subject = data?.subject || 'Test Email from UltraFlex';
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(`${API_URL}/api/crm/automations/send-test-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ to_email: testEmail, subject, html_content: html }),
      });
      const result = await res.json();
      setTestResult(result);
    } catch (err) {
      setTestResult({ status: 'error', message: err.message });
    }
    setSendingTest(false);
  };

  const handleSave = () => {
    const html = generateFullHtml(blocks, brand);
    const plainText = blocks.filter(b => b.type === 'text').map(b => b.content.text || '').join('\n\n');
    onSave({ emailBlocks: blocks, brand, emailHtml: html, body: plainText });
  };

  // Preset selector
  if (showPresets) {
    return (
      <div className="fixed inset-0 z-50 bg-[#0b0f1a] flex flex-col" data-testid="email-preset-picker">
        <div className="h-14 bg-[#111827] border-b border-[#1e293b] flex items-center justify-between px-6 flex-shrink-0">
          <div className="flex items-center gap-3">
            <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-[#1e293b] text-gray-400 hover:text-white"><ChevronLeft size={18} /></button>
            <div className="h-6 w-px bg-[#1e293b]" />
            <Sparkles size={18} className="text-[#dc2626]" />
            <h1 className="text-white font-semibold text-sm">Choose an Email Layout</h1>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 max-w-7xl mx-auto">
            {EMAIL_PRESETS.map(preset => (
              <button key={preset.id} onClick={() => loadPreset(preset)}
                className="group text-left rounded-xl border border-[#1e293b] hover:border-[#dc2626] bg-[#0f172a] hover:bg-[#111827] transition-all overflow-hidden"
                data-testid={`email-preset-${preset.id}`}>
                {/* Mini preview */}
                <div className="h-32 bg-[#1a1a2e] border-b border-[#1e293b] flex items-center justify-center p-3 overflow-hidden">
                  {preset.id !== 'blank' ? (
                    <div className="w-full max-w-[120px] bg-white rounded shadow-sm overflow-hidden transform scale-[0.6] origin-top">
                      {preset.blocks.slice(0, 3).map((b, i) => (
                        <div key={i} style={{ 
                          background: b.type === 'header' ? (b.content.bgColor || '#dc2626') : b.type === 'footer' ? '#111827' : '#fff',
                          padding: b.type === 'button' ? '4px' : '6px 8px',
                          textAlign: b.content?.alignment || 'left',
                        }}>
                          {b.type === 'header' && <div style={{ color: '#fff', fontSize: '7px', fontWeight: 700 }}>{b.content.headline}</div>}
                          {b.type === 'text' && <div style={{ fontSize: '4px', color: '#666', lineHeight: 1.4 }}>{(b.content.text || '').substring(0, 60)}...</div>}
                          {b.type === 'button' && <div style={{ background: b.content.bgColor, color: '#fff', fontSize: '5px', padding: '2px 6px', borderRadius: '2px', display: 'inline-block' }}>{b.content.text}</div>}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <Plus size={24} className="text-gray-600" />
                  )}
                </div>
                <div className="p-4">
                  <h3 className="text-white font-semibold text-xs mb-1 group-hover:text-[#dc2626] transition-colors">{preset.name}</h3>
                  <p className="text-gray-500 text-[10px]">{preset.desc}</p>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 bg-[#0b0f1a] flex flex-col" data-testid="email-designer">
      {/* Top toolbar */}
      <div className="h-14 bg-[#111827] border-b border-[#1e293b] flex items-center justify-between px-4 flex-shrink-0">
        <div className="flex items-center gap-3">
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-[#1e293b] text-gray-400 hover:text-white" data-testid="email-designer-back">
            <ChevronLeft size={18} />
          </button>
          <div className="h-6 w-px bg-[#1e293b]" />
          <Mail size={18} className="text-[#dc2626]" />
          <h1 className="text-white font-semibold text-sm">Email Designer</h1>
        </div>

        <div className="flex items-center gap-2">
          {/* Preview toggle */}
          <div className="flex bg-[#1e293b] rounded-lg p-0.5">
            <button onClick={() => setPreviewMode('desktop')}
              className={`p-1.5 rounded-md transition-all ${previewMode === 'desktop' ? 'bg-[#dc2626] text-white' : 'text-gray-400 hover:text-white'}`}>
              <Monitor size={14} />
            </button>
            <button onClick={() => setPreviewMode('mobile')}
              className={`p-1.5 rounded-md transition-all ${previewMode === 'mobile' ? 'bg-[#dc2626] text-white' : 'text-gray-400 hover:text-white'}`}>
              <Smartphone size={14} />
            </button>
          </div>
          <button onClick={() => setShowPresets(true)} className="px-3 py-1.5 bg-[#1e293b] text-gray-400 hover:text-white rounded-lg text-xs" data-testid="email-change-layout">
            <LayoutTemplate size={14} className="inline mr-1.5" />Change Layout
          </button>
          <button onClick={() => setSendTestOpen(true)} disabled={blocks.length === 0}
            className="px-3 py-1.5 bg-[#1e293b] text-gray-400 hover:text-white rounded-lg text-xs disabled:opacity-40" data-testid="send-test-email-btn">
            <Mail size={14} className="inline mr-1.5" />Send Test
          </button>
          <button onClick={handleSave} className="px-4 py-1.5 bg-[#E31837] text-white rounded-lg text-xs font-medium hover:bg-[#c41530]" data-testid="email-designer-save">
            <Check size={14} className="inline mr-1.5" />Apply Design
          </button>
        </div>
      </div>

      {/* Main layout */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left: Block editor panel */}
        <div className="w-[340px] bg-[#111827] border-r border-[#1e293b] flex flex-col flex-shrink-0">
          {/* Tabs */}
          <div className="flex border-b border-[#1e293b]">
            {[{ id: 'blocks', label: 'Content' }, { id: 'brand', label: 'Brand' }].map(tab => (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                className={`flex-1 py-2.5 text-xs font-medium transition-all ${activeTab === tab.id ? 'text-[#dc2626] border-b-2 border-[#dc2626]' : 'text-gray-400 hover:text-white'}`}>
                {tab.label}
              </button>
            ))}
          </div>

          <div className="flex-1 overflow-y-auto">
            {activeTab === 'blocks' ? (
              <div className="p-3 space-y-3">
                {/* Add block palette */}
                <div>
                  <p className="text-gray-500 text-[10px] font-medium uppercase tracking-wider mb-2">Add Content Block</p>
                  <div className="grid grid-cols-3 gap-1.5">
                    {BLOCK_TYPES.map(bt => (
                      <button key={bt.type} onClick={() => addBlock(bt.type)}
                        className="p-2 rounded-lg bg-[#0f172a] border border-[#1e293b] hover:border-[#dc2626] text-center transition-all group"
                        data-testid={`add-block-${bt.type}`}>
                        <bt.icon size={16} className="mx-auto text-gray-500 group-hover:text-[#dc2626] transition-colors mb-1" />
                        <span className="text-[9px] text-gray-400 group-hover:text-white block leading-tight">{bt.label}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Block list */}
                {blocks.length > 0 && (
                  <div>
                    <p className="text-gray-500 text-[10px] font-medium uppercase tracking-wider mb-2">Email Structure ({blocks.length} blocks)</p>
                    <div className="space-y-1.5">
                      {blocks.map((block, idx) => (
                        <BlockEditor key={block.id} block={block} brand={brand}
                          onChange={b => updateBlock(idx, b)}
                          onDelete={() => deleteBlock(idx)}
                          onMoveUp={() => moveBlock(idx, -1)}
                          onMoveDown={() => moveBlock(idx, 1)}
                          isFirst={idx === 0} isLast={idx === blocks.length - 1} />
                      ))}
                    </div>
                  </div>
                )}

                {blocks.length === 0 && (
                  <div className="text-center py-8">
                    <Mail size={32} className="mx-auto text-gray-600 mb-3" />
                    <p className="text-gray-400 text-xs mb-1">No content blocks yet</p>
                    <p className="text-gray-600 text-[10px]">Click a block type above or choose a preset layout</p>
                  </div>
                )}
              </div>
            ) : (
              /* Brand settings tab */
              <div className="p-4 space-y-4">
                <p className="text-gray-500 text-[10px] font-medium uppercase tracking-wider">Brand Settings</p>
                <div>
                  <label className="text-gray-400 text-xs block mb-1.5">Primary Color</label>
                  <div className="flex items-center gap-2">
                    <input type="color" value={brand.primaryColor} onChange={e => setBrand({ ...brand, primaryColor: e.target.value })} className="w-10 h-10 rounded-lg cursor-pointer bg-transparent border-0" />
                    <input value={brand.primaryColor} onChange={e => setBrand({ ...brand, primaryColor: e.target.value })} className="flex-1 bg-[#0f172a] border border-[#1e293b] rounded px-3 py-2 text-white text-xs font-mono" />
                  </div>
                </div>
                <div>
                  <label className="text-gray-400 text-xs block mb-1.5">Email Background</label>
                  <div className="flex items-center gap-2">
                    <input type="color" value={brand.bgColor} onChange={e => setBrand({ ...brand, bgColor: e.target.value })} className="w-10 h-10 rounded-lg cursor-pointer bg-transparent border-0" />
                    <input value={brand.bgColor} onChange={e => setBrand({ ...brand, bgColor: e.target.value })} className="flex-1 bg-[#0f172a] border border-[#1e293b] rounded px-3 py-2 text-white text-xs font-mono" />
                  </div>
                </div>
                <div>
                  <label className="text-gray-400 text-xs block mb-1.5">Text Color</label>
                  <div className="flex items-center gap-2">
                    <input type="color" value={brand.textColor} onChange={e => setBrand({ ...brand, textColor: e.target.value })} className="w-10 h-10 rounded-lg cursor-pointer bg-transparent border-0" />
                    <input value={brand.textColor} onChange={e => setBrand({ ...brand, textColor: e.target.value })} className="flex-1 bg-[#0f172a] border border-[#1e293b] rounded px-3 py-2 text-white text-xs font-mono" />
                  </div>
                </div>
                <div>
                  <label className="text-gray-400 text-xs block mb-1.5">Font Family</label>
                  <select value={brand.fontFamily} onChange={e => setBrand({ ...brand, fontFamily: e.target.value })}
                    className="w-full bg-[#0f172a] border border-[#1e293b] rounded px-3 py-2 text-white text-xs">
                    <option value="Arial, Helvetica, sans-serif">Arial / Helvetica</option>
                    <option value="Georgia, 'Times New Roman', serif">Georgia / Serif</option>
                    <option value="'Trebuchet MS', sans-serif">Trebuchet MS</option>
                    <option value="Verdana, Geneva, sans-serif">Verdana</option>
                    <option value="'Courier New', monospace">Courier (Monospace)</option>
                  </select>
                </div>
                <div>
                  <label className="text-gray-400 text-xs block mb-1.5">Company Name</label>
                  <input value={brand.companyName} onChange={e => setBrand({ ...brand, companyName: e.target.value })}
                    className="w-full bg-[#0f172a] border border-[#1e293b] rounded px-3 py-2 text-white text-xs" />
                </div>
                <div>
                  <label className="text-gray-400 text-xs block mb-1.5">Logo URL</label>
                  <input value={brand.logoUrl} onChange={e => setBrand({ ...brand, logoUrl: e.target.value })} placeholder="https://..."
                    className="w-full bg-[#0f172a] border border-[#1e293b] rounded px-3 py-2 text-white text-xs" />
                </div>
                {/* Preview palette */}
                <div className="p-3 rounded-lg border border-[#1e293b] bg-[#0f172a]">
                  <p className="text-gray-500 text-[10px] mb-2">Color Preview</p>
                  <div className="flex gap-2">
                    <div className="w-10 h-10 rounded" style={{ background: brand.primaryColor }} title="Primary" />
                    <div className="w-10 h-10 rounded" style={{ background: brand.bgColor }} title="Background" />
                    <div className="w-10 h-10 rounded border border-[#1e293b]" style={{ background: '#ffffff' }} title="Content BG" />
                    <div className="w-10 h-10 rounded flex items-center justify-center" style={{ background: brand.textColor }}>
                      <span style={{ color: '#fff', fontSize: 9 }}>Aa</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Live preview area */}
        <div className="flex-1 bg-[#1a1a2e] overflow-y-auto flex justify-center p-6" data-testid="email-preview-area">
          <div style={{ 
            width: previewMode === 'mobile' ? '375px' : '620px', 
            transition: 'width 0.3s',
            maxWidth: '100%',
          }}>
            {/* Email client chrome */}
            <div className="bg-[#1e293b] rounded-t-xl p-3 flex items-center gap-2">
              <div className="flex gap-1.5">
                <div className="w-3 h-3 rounded-full bg-[#ef4444]" />
                <div className="w-3 h-3 rounded-full bg-[#f59e0b]" />
                <div className="w-3 h-3 rounded-full bg-[#22c55e]" />
              </div>
              <div className="flex-1 bg-[#0f172a] rounded px-3 py-1 text-gray-500 text-[10px] ml-2">inbox — UltraFlex Gym</div>
            </div>

            {/* Rendered email */}
            <div ref={previewRef} className="bg-white rounded-b-xl overflow-hidden shadow-2xl"
              style={{ background: brand.bgColor }}>
              <div style={{ maxWidth: '600px', margin: '0 auto', background: '#ffffff' }}
                dangerouslySetInnerHTML={{ __html: blocks.map(b => renderBlockHtml(b, brand)).join('') }} />
              {blocks.length === 0 && (
                <div className="p-12 text-center">
                  <Mail size={40} className="mx-auto text-gray-300 mb-3" />
                  <p className="text-gray-400 text-sm">Your email preview will appear here</p>
                  <p className="text-gray-300 text-xs mt-1">Add content blocks from the left panel</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Send Test Email Modal */}
      {sendTestOpen && (
        <div className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center" onClick={() => { setSendTestOpen(false); setTestResult(null); }}>
          <div className="bg-[#111827] rounded-xl border border-[#1e293b] p-6 w-full max-w-md mx-4 shadow-2xl" onClick={e => e.stopPropagation()} data-testid="send-test-modal">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Mail size={18} className="text-[#E31837]" />
                <h3 className="text-white font-semibold text-sm">Send Test Email</h3>
              </div>
              <button onClick={() => { setSendTestOpen(false); setTestResult(null); }} className="p-1 rounded hover:bg-[#1e293b] text-gray-400"><X size={16} /></button>
            </div>
            <p className="text-gray-400 text-xs mb-4">Send a preview of this email to check how it looks in your inbox. Subject will be prefixed with [TEST].</p>
            <div className="mb-4">
              <label className="text-gray-400 text-xs block mb-1.5">Recipient Email</label>
              <input type="email" value={testEmail} onChange={e => setTestEmail(e.target.value)} placeholder="your.email@example.com"
                className="w-full bg-[#0b0f1a] border border-[#1e293b] rounded-lg px-3 py-2.5 text-white text-sm" data-testid="test-email-input" />
            </div>

            {testResult && (
              <div className={`p-3 rounded-lg mb-4 text-xs ${testResult.status === 'sent' ? 'bg-emerald-500/10 border border-emerald-500/20 text-emerald-400' : testResult.status === 'skipped' ? 'bg-amber-500/10 border border-amber-500/20 text-amber-400' : 'bg-red-500/10 border border-red-500/20 text-red-400'}`}>
                {testResult.message}
              </div>
            )}

            <div className="flex gap-2">
              <button onClick={() => { setSendTestOpen(false); setTestResult(null); }} className="flex-1 py-2 border border-[#1e293b] text-gray-400 rounded-lg text-sm hover:text-white hover:border-[#2a3a5a]">Cancel</button>
              <button onClick={handleSendTest} disabled={!testEmail || sendingTest}
                className="flex-1 py-2 bg-[#E31837] text-white rounded-lg text-sm font-medium disabled:opacity-50 flex items-center justify-center gap-2" data-testid="send-test-btn">
                {sendingTest ? <><RefreshCw size={14} className="animate-spin" />Sending...</> : <><Mail size={14} />Send Test</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmailDesigner;
export { generateFullHtml, DEFAULT_BRAND };
